import { useState } from "react";
import { Download, Send, ShieldCheck, Undo2, Loader2, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr, inr } from "@/lib/api";

const getPdf = async (q) => {
  const { data } = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
  return new File([data], `${q.quote_no.replace(/\//g, "-")}.pdf`, { type: "application/pdf" });
};
const saveFile = (file) => {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const waNumber = (p) => {
  const d = (p || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};

export const ShareActions = ({ q }) => {
  const [busy, setBusy] = useState("");
  const text = `Dear ${q.customer.contact_person || "Sir/Madam"},\nPlease find attached our quotation ${q.quote_no} for ${q.customer.hospital_name} amounting to ${inr(q.grand_total)} (incl. GST).\n\nRegards,\n${q.created_by_name || ""}\nUpkar Healthcare India Pvt. Ltd.\nMo. 9839708442, 9838889966`;

  const run = async (kind) => {
    setBusy(kind);
    try {
      const file = await getPdf(q);
      if (kind === "pdf") {
        saveFile(file);
      } else if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text, title: q.quote_no });
      } else {
        saveFile(file);
        window.open(`https://wa.me/${waNumber(q.customer.phone_number)}?text=${encodeURIComponent(text)}`, "_blank");
        toast.info("PDF downloaded — attach it in the WhatsApp chat that just opened");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(formatErr(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <Button variant="outline" onClick={() => run("pdf")} disabled={!!busy} data-testid="quote-download-pdf-button">
        {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" />Download PDF</>}
      </Button>
      <Button onClick={() => run("wa")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-whatsapp-share-button">
        {busy === "wa" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><MessageCircle className="h-4 w-4 mr-2" />Share on WhatsApp</>}
      </Button>
    </div>
  );
};

export const DirectorActions = ({ q, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/quotations/${q.id}/${kind}`, { note });
      toast.success(kind === "approve" ? "Quotation approved — staff can now share it" : "Sent back to staff for changes");
      onDone(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="space-y-2 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="quote-director-actions">
      <div className="font-heading font-bold text-slate-900">Director Review</div>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to staff (required when requesting changes)" className="bg-white text-sm" data-testid="quote-director-note-input" />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => act("reject")} disabled={!!busy} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="quote-reject-button">
          {busy === "reject" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Undo2 className="h-4 w-4 mr-2" />Request Changes</>}
        </Button>
        <Button onClick={() => act("approve")} disabled={!!busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quote-approve-button">
          {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" />Approve</>}
        </Button>
      </div>
    </div>
  );
};

export const EditActions = ({ onSave, onSubmit, onDelete, busy, isNew, isDirector, canSubmit }) => (
  <div className="space-y-2">
    <div className="grid grid-cols-2 gap-2">
      <Button variant="outline" onClick={onSave} disabled={!!busy} data-testid="quote-save-draft-button">
        {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Draft"}
      </Button>
      <Button onClick={onSubmit} disabled={!!busy || !canSubmit} className="bg-sky-600 hover:bg-sky-700" data-testid="quote-submit-button">
        {busy === "submit" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" />{isDirector ? "Save & Approve" : "Send for Approval"}</>}
      </Button>
    </div>
    {!isNew && (
      <Button variant="ghost" onClick={onDelete} disabled={!!busy} className="w-full text-rose-600" data-testid="quote-delete-button"><Trash2 className="h-4 w-4 mr-2" />Delete quotation</Button>
    )}
  </div>
);
